import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Loader2, CheckCircle2, XCircle, Power, PowerOff, AlertTriangle,
} from "lucide-react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import PropertyStatusBadge, { PROPERTY_TYPE_LABELS, GENDER_LABELS, PROPERTY_STATUS_LABELS } from "@/components/property/PropertyStatusBadge";
import SanquaBadge from "@/components/property/SanquaBadge";
import AmenityDisplay from "@/components/property/AmenityDisplay";
import AdminPhotoModeration from "@/components/property/AdminPhotoModeration";
import NearbyAuto from "@/components/property/NearbyAuto";
import { AmenityIcon } from "@/components/property/amenityMeta";
import { ROOM_STATUS_LABELS, ROOM_STATUS_STYLES, formatRupiah } from "@/components/property/roomMeta";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-border">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground text-right">{value || "-"}</span>
    </div>
  );
}

export default function AdminPropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [amenities, setAmenities] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [acting, setActing] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");

  const loadProperty = useCallback(() => {
    return apiClient.get(`/admin/properties/${id}`)
      .then((r) => setP(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [id]);

  useEffect(() => {
    Promise.all([
      loadProperty(),
      apiClient.get(`/properties/${id}/amenities`).then((r) => setAmenities(r.data || [])).catch(() => {}),
      apiClient.get(`/properties/${id}/rooms`).then((r) => setRooms(r.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [id, loadProperty]);

  const doAction = async (action) => {
    setActing(action);
    try {
      await apiClient.post(`/admin/properties/${id}/${action}`);
      toast.success(
        action === "approve" ? "Property disetujui & dipublikasikan"
          : action === "activate" ? "Property diaktifkan kembali"
          : "Property dinonaktifkan"
      );
      await loadProperty();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setActing("");
    }
  };

  const toggleSanqua = async () => {
    setActing("sanqua");
    try {
      await apiClient.patch(`/admin/properties/${id}/sanqua`, { sanqua_partner: !p.sanqua_partner });
      toast.success(!p.sanqua_partner ? "Ditandai sebagai Mitra Sanqua" : "Badge Sanqua dinonaktifkan");
      await loadProperty();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setActing("");
    }
  };

  const doReject = async () => {
    if (reason.trim().length < 3) { toast.error("Alasan penolakan wajib diisi (min. 3 karakter)"); return; }
    setActing("reject");
    try {
      await apiClient.post(`/admin/properties/${id}/reject`, { rejection_reason: reason.trim() });
      toast.success("Property ditolak");
      setRejectOpen(false);
      setReason("");
      await loadProperty();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setActing("");
    }
  };

  return (
    <AdminLayout testid="admin-property-detail-page">
      <button onClick={() => navigate("/admin/properties")} className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors mb-4" data-testid="admin-back-link">
        <ArrowLeft size={16} /> Moderasi Properti
      </button>

      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : error ? (
        <div className="rounded-xl bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive" data-testid="admin-detail-error">{error}</div>
      ) : (
        <div>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="admin-property-name">{p.name}</h1>
                <PropertyStatusBadge status={p.status} testid="admin-property-status" />
              </div>
              <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
                <MapPin size={15} /> {p.location?.address}, {p.location?.district_name}, {p.location?.city_name}, {p.location?.province_name}
              </p>
            </div>
          </div>

          {/* Moderation action bar */}
          <div className="mt-5 flex flex-wrap gap-2" data-testid="moderation-actions">
            {p.status === "pending" && (
              <>
                <button onClick={() => doAction("approve")} disabled={!!acting} data-testid="approve-button"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-60">
                  {acting === "approve" ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Approve
                </button>
                <button onClick={() => setRejectOpen(true)} disabled={!!acting} data-testid="reject-button"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors disabled:opacity-60">
                  <XCircle size={16} /> Reject
                </button>
              </>
            )}
            {p.status === "published" && (
              <button onClick={() => doAction("deactivate")} disabled={!!acting} data-testid="deactivate-button"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-border text-destructive font-semibold hover:bg-destructive/10 transition-colors disabled:opacity-60">
                {acting === "deactivate" ? <Loader2 size={16} className="animate-spin" /> : <PowerOff size={16} />} Nonaktifkan
              </button>
            )}
            {p.status === "inactive" && (
              <button onClick={() => doAction("activate")} disabled={!!acting} data-testid="activate-button"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-60">
                {acting === "activate" ? <Loader2 size={16} className="animate-spin" /> : <Power size={16} />} Aktifkan
              </button>
            )}
          </div>

          {/* Rejection reason (history) */}
          {p.status === "rejected" && p.rejection_reason && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="admin-rejection-reason">
              <AlertTriangle className="text-red-600 shrink-0" size={18} />
              <div>
                <p className="font-semibold text-red-800">Alasan Penolakan</p>
                <p className="text-sm text-red-700">{p.rejection_reason}</p>
                {p.rejected_by && <p className="mt-1 text-xs text-red-600">Oleh {p.rejected_by}</p>}
              </div>
            </div>
          )}

          {/* Sanqua partnership (admin-controlled) */}
          <section className="mt-6 bg-card border border-border rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4" data-testid="admin-sanqua">
            <div className="flex items-center gap-3">
              <div>
                <h3 className="font-heading text-lg font-semibold">Kemitraan Air Minum Sanqua</h3>
                <p className="text-sm text-muted-foreground">Tampilkan badge mitra Sanqua di web-site & halaman detail properti ini.</p>
              </div>
              {p.sanqua_partner && <SanquaBadge testid="admin-sanqua-badge" />}
            </div>
            <button onClick={toggleSanqua} disabled={!!acting} data-testid="admin-sanqua-toggle"
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold transition-colors disabled:opacity-60 ${
                p.sanqua_partner ? "border border-border hover:bg-secondary/60" : "bg-sky-600 text-white hover:bg-sky-700"
              }`}>
              {acting === "sanqua" ? <Loader2 size={16} className="animate-spin" /> : null}
              {p.sanqua_partner ? "Nonaktifkan Badge" : "Tandai sebagai Mitra Sanqua"}
            </button>
          </section>

          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <section className="bg-card border border-border rounded-2xl p-6">
              <h3 className="font-heading text-lg font-semibold mb-3">Informasi Property</h3>
              <Row label="Tipe Property" value={PROPERTY_TYPE_LABELS[p.property_type]} />
              <Row label="Gender" value={GENDER_LABELS[p.gender_type]} />
              <Row label="Status" value={PROPERTY_STATUS_LABELS[p.status] || p.status} />
              <div className="pt-3">
                <p className="text-sm text-muted-foreground">Deskripsi</p>
                <p className="mt-1 text-sm text-foreground whitespace-pre-line">{p.description}</p>
              </div>
            </section>
            <section className="bg-card border border-border rounded-2xl p-6">
              <h3 className="font-heading text-lg font-semibold mb-3">Owner</h3>
              <Row label="Nama" value={p.owner?.name} />
              <Row label="Email" value={p.owner?.email} />
              <Row label="Telepon" value={p.owner?.phone} />
              <h3 className="font-heading text-lg font-semibold mb-3 mt-5">Lokasi & Koordinat</h3>
              <Row label="Provinsi" value={p.location?.province_name} />
              <Row label="Kota/Kabupaten" value={p.location?.city_name} />
              <Row label="Kecamatan" value={p.location?.district_name} />
              <Row label="Kelurahan/Desa" value={p.location?.subdistrict_name} />
              <Row label="Alamat" value={p.location?.address} />
              <Row label="Latitude" value={p.latitude} />
              <Row label="Longitude" value={p.longitude} />
            </section>
          </div>

          <section className="mt-6 bg-card border border-border rounded-2xl p-6" data-testid="admin-rooms">
            <h3 className="font-heading text-lg font-semibold mb-3">Daftar Kamar ({rooms.length})</h3>
            {rooms.length === 0 ? (
              <p className="text-sm text-muted-foreground">Belum ada kamar.</p>
            ) : (
              <div className="space-y-3">
                {rooms.map((r, i) => (
                  <div key={r.id} className="rounded-xl border border-border p-4" data-testid={`admin-room-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{r.room_number}</span>
                      {r.name && <span className="text-sm text-muted-foreground">· {r.name}</span>}
                      <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${ROOM_STATUS_STYLES[r.status] || ""}`}>
                        {ROOM_STATUS_LABELS[r.status] || r.status}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 text-sm text-muted-foreground">
                      <span className="font-semibold text-primary">{formatRupiah(r.price_monthly)}/bln</span>
                      {r.size != null && <span>{r.size} m²</span>}
                      {r.floor != null && <span>Lantai {r.floor}</span>}
                    </div>
                    {r.amenities?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {r.amenities.map((a) => (
                          <span key={a.id} className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/40 px-2.5 py-1 text-xs">
                            <AmenityIcon name={a.icon} size={12} className="text-primary" /> {a.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="mt-6 bg-card border border-border rounded-2xl p-6" data-testid="admin-amenities">
            <h3 className="font-heading text-lg font-semibold mb-3">Fasilitas Property</h3>
            <AmenityDisplay amenities={amenities} testid="admin-amenity-display" />
          </section>

          <section className="mt-6 bg-card border border-border rounded-2xl p-6" data-testid="admin-photos">
            <h3 className="font-heading text-lg font-semibold mb-3">Foto (Moderasi)</h3>
            <AdminPhotoModeration propertyId={p.id} />
          </section>

          <section className="mt-6 bg-card border border-border rounded-2xl p-6" data-testid="admin-nearby">
            <h3 className="font-heading text-lg font-semibold mb-3">Tempat di Sekitar</h3>
            <NearbyAuto propertyId={p.id} />
          </section>
        </div>
      )}

      {/* Reject modal */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Tolak Property</DialogTitle>
            <DialogDescription>Berikan alasan penolakan agar Owner dapat memperbaiki listing.</DialogDescription>
          </DialogHeader>
          <textarea
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="reject-reason-input"
            placeholder="Contoh: Deskripsi belum lengkap, foto belum tersedia..."
            className="w-full rounded-xl border border-border bg-secondary/40 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary focus:bg-background"
          />
          <div className="flex items-center gap-3 pt-2">
            <button onClick={doReject} disabled={acting === "reject"} data-testid="reject-confirm-button"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors disabled:opacity-60">
              {acting === "reject" && <Loader2 size={16} className="animate-spin" />} Tolak Property
            </button>
            <button onClick={() => setRejectOpen(false)} data-testid="reject-cancel-button"
              className="px-5 py-2.5 rounded-full border border-border font-semibold hover:bg-secondary/60 transition-colors">
              Batal
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
